import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useEffect, useRef, useState } from 'react';
import { Button, Input, SimpleTable, useToast } from '@src/components/ui';
import AdminLayout from '../AdminLayout';
import { fetchSalesReps, updateSalesRep } from '../../../api/SalesRepAdminApi';
import { createSalesRepColumns } from './tableColumns';
import { mapSalesRep } from './types';
import SalesRepFormPanel from './SalesRepFormPanel';
import SalesSummaryPanel from './SalesSummaryPanel';
import styles from './SalesReps.module.scss';
const extractRows = (body) => (Array.isArray(body) ? body : body?.results || []);
const SalesReps = () => {
    const { showToast } = useToast();
    const [loading, setLoading] = useState(true);
    const [reps, setReps] = useState([]);
    // Unfiltered list used for the "Reports To" options, independent of the table's search/filter.
    const [allReps, setAllReps] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [isActiveFilter, setIsActiveFilter] = useState('');
    const [appliedIsActive, setAppliedIsActive] = useState('');
    const [isRunningBulkAction, setIsRunningBulkAction] = useState(false);
    const [formState, setFormState] = useState(null);
    const latestRequestIdRef = useRef(0);
    const loadReps = useCallback(async (search, isActive) => {
        const requestId = ++latestRequestIdRef.current;
        try {
            setLoading(true);
            const response = await fetchSalesReps({
                search: search || undefined,
                is_active: isActive === '' ? undefined : isActive === 'true',
            });
            if (requestId !== latestRequestIdRef.current)
                return;
            setReps(extractRows(response.body).map(mapSalesRep));
        }
        catch (error) {
            if (requestId !== latestRequestIdRef.current)
                return;
            console.error('Error loading sales reps:', error);
            showToast({ message: 'Failed to load sales reps', variant: 'error', noBorder: true });
            setReps([]);
        }
        finally {
            if (requestId === latestRequestIdRef.current)
                setLoading(false);
        }
    }, [showToast]);
    const loadAllReps = useCallback(async () => {
        try {
            const response = await fetchSalesReps();
            setAllReps(extractRows(response.body).map(mapSalesRep));
        }
        catch (error) {
            console.error('Error loading sales rep options:', error);
            setAllReps([]);
        }
    }, []);
    useEffect(() => {
        const timeoutId = window.setTimeout(() => loadReps(searchTerm, appliedIsActive), 300);
        return () => window.clearTimeout(timeoutId);
    }, [loadReps, searchTerm, appliedIsActive]);
    useEffect(() => {
        loadAllReps();
    }, [loadAllReps]);
    const refresh = () => {
        loadReps(searchTerm, appliedIsActive);
        loadAllReps();
    };
    const handleSaved = () => {
        setFormState(null);
        refresh();
    };
    const handleReset = () => {
        setIsActiveFilter('');
        setAppliedIsActive('');
    };
    const runBulkUpdate = async (selectedRows, isActive) => {
        if (!selectedRows.length || isRunningBulkAction)
            return;
        setIsRunningBulkAction(true);
        try {
            const results = await Promise.allSettled(selectedRows.map((row) => updateSalesRep(row.id, { is_active: isActive })));
            const failed = results.filter((result) => result.status === 'rejected').length;
            const succeeded = results.length - failed;
            if (succeeded) {
                showToast({
                    message: `Updated ${succeeded} sales rep${succeeded === 1 ? '' : 's'} as ${isActive ? 'active' : 'inactive'}`,
                    variant: failed ? 'warning' : 'success',
                    noBorder: true,
                });
            }
            if (failed) {
                showToast({
                    message: `Failed to update ${failed} sales rep${failed === 1 ? '' : 's'}`,
                    variant: 'error',
                    noBorder: true,
                });
            }
            refresh();
        }
        finally {
            setIsRunningBulkAction(false);
        }
    };
    const bulkActions = [
        { label: 'Activate', action: (rows) => runBulkUpdate(rows, true) },
        { label: 'Deactivate', action: (rows) => runBulkUpdate(rows, false) },
    ];
    return (_jsxs(AdminLayout, { children: [loading && _jsx("div", { children: "Loading..." }), _jsxs("div", { className: styles.tableWrapper, children: [_jsxs("div", { className: styles.filterBar, children: [_jsxs("div", { className: styles.filterField, children: [_jsx("label", { htmlFor: "sales-rep-is-active", children: "Is Active" }), _jsxs("select", { id: "sales-rep-is-active", value: isActiveFilter, onChange: (event) => setIsActiveFilter(event.target.value), children: [_jsx("option", { value: "", children: "All" }), _jsx("option", { value: "true", children: "Active" }), _jsx("option", { value: "false", children: "Inactive" })] })] }), _jsxs("div", { className: styles.filterActions, children: [_jsx(Button, { variant: "primaryV2", size: "small", label: "Apply Filters", onClick: () => setAppliedIsActive(isActiveFilter) }), _jsx(Button, { variant: "secondaryV2", size: "small", label: "Reset", onClick: handleReset })] }), _jsx("div", { className: styles.searchRow, children: _jsxs("div", { className: styles.searchInputWrapper, children: [_jsx(Input, { value: searchTerm, onChange: (value) => setSearchTerm(value || ''), placeholder: "Search by ID, name, email, or territory", width: "420px", showIcon: true, iconWidth: "16px", iconHeight: "16px" }), !!searchTerm && (_jsx("button", { type: "button", className: styles.clearButton, onClick: () => setSearchTerm(''), "aria-label": "Clear search", children: "x" }))] }) })] }), _jsx(SimpleTable, { columns: createSalesRepColumns(), data: reps, rowHeight: "60px", tableHeading: "Sales Reps", titleStyle: {
                            color: '#000',
                            fontFamily: 'Nunito',
                            fontSize: '24px',
                            fontStyle: 'normal',
                            fontWeight: 600,
                            lineHeight: '28px',
                        }, enableSelection: true, hideTopBorder: true, enablePagination: true, paginationText: "Sales reps per page", pageSize: 20, onRowClick: (rep) => setFormState({ rep }), bulkActions: bulkActions, customButton: _jsx(Button, { variant: "primaryV2", size: "small", label: "+ Add Sales Rep", onClick: () => setFormState({ rep: null }) }) })] }), _jsx(SalesSummaryPanel, {}), formState && (_jsx(SalesRepFormPanel, { rep: formState.rep, reps: allReps, onClose: () => setFormState(null), onSaved: handleSaved }, formState.rep?.id ?? 'new'))] }));
};
export default SalesReps;
